import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useTranslation } from '../../utils/i18n-utils';
import { 
  getExpenseCategories,
  createExpenseCategory,
  updateExpenseCategory,
  deleteExpenseCategory,
  ExpenseCategory
} from '../../api/accounting';
import { format } from 'date-fns';

const ExpenseCategories: React.FC = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [currentCategory, setCurrentCategory] = useState<Partial<ExpenseCategory>>({
    name: '',
    description: '',
    isActive: true
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await getExpenseCategories();
      setCategories(data);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch expense categories:', err);
      setError('Failed to load expense categories. Please try again later.');
      showSnackbar('Failed to load expense categories', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = (mode: 'add' | 'edit', category?: ExpenseCategory) => {
    setDialogMode(mode);
    
    if (mode === 'edit' && category) {
      setCurrentCategory(category);
    } else {
      setCurrentCategory({
        name: '',
        description: '',
        isActive: true
      });
    }
    
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentCategory({
      name: '',
      description: '',
      isActive: true
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentCategory({
      ...currentCategory,
      [name]: value
    });
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentCategory({
      ...currentCategory,
      isActive: e.target.checked
    });
  };

  const handleSave = async () => {
    try {
      if (!currentCategory.name || !currentCategory.name.trim()) {
        showSnackbar('Category name is required', 'error');
        return;
      }

      if (dialogMode === 'add') {
        await createExpenseCategory(currentCategory);
        showSnackbar('Expense category created successfully', 'success');
      } else {
        if (!currentCategory.id) {
          showSnackbar('Category ID is missing', 'error');
          return;
        }
        await updateExpenseCategory(currentCategory.id, currentCategory);
        showSnackbar('Expense category updated successfully', 'success');
      }
      
      handleCloseDialog();
      fetchCategories();
    } catch (err: any) {
      console.error('Failed to save expense category:', err);
      const errorMessage = err.response?.data?.message || 'Failed to save expense category';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleDelete = async (category: ExpenseCategory) => {
    if (!category.id) return;

    try {
      await deleteExpenseCategory(category.id);
      showSnackbar('Expense category deleted successfully', 'success');
      fetchCategories();
    } catch (err: any) {
      console.error('Failed to delete expense category:', err);
      const errorMessage = err.response?.data?.message || 'Failed to delete expense category. It may be in use by expenses.';
      showSnackbar(errorMessage, 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          {t('expenseCategories.title') || 'Expense Categories'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('add')}
        >
          {t('expenseCategories.addCategory') || 'Add Category'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{
        borderRadius: 3,
        boxShadow: '0px 2px 8px rgba(16, 29, 104, 0.08)',
        overflow: 'hidden',
      }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{
                backgroundColor: '#F9FAFB',
                '& .MuiTableCell-head': {
                  fontWeight: 600,
                  color: '#101D68',
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                },
              }}>
                <TableCell>{t('expenseCategories.name') || 'Name'}</TableCell>
                <TableCell>{t('expenseCategories.description') || 'Description'}</TableCell>
                <TableCell align="center">{t('expenseCategories.status') || 'Status'}</TableCell>
                <TableCell>{t('expenseCategories.createdAt') || 'Created At'}</TableCell>
                <TableCell align="right">{t('expenseCategories.actions') || 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {t('expenseCategories.noCategories') || 'No expense categories found'}
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((category) => (
                  <TableRow key={category.id}>
                    <TableCell>{category.name}</TableCell>
                    <TableCell>{category.description || '-'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={category.isActive ? (t('common.active') || 'Active') : (t('common.inactive') || 'Inactive')}
                        color={category.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {format(new Date(category.createdAt), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('common.edit') || 'Edit'}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog('edit', category)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete') || 'Delete'}>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(category)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' 
            ? (t('expenseCategories.addCategory') || 'Add Expense Category')
            : (t('expenseCategories.editCategory') || 'Edit Expense Category')
          }
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('expenseCategories.name') || 'Name'}
            name="name"
            fullWidth
            variant="outlined"
            value={currentCategory.name || ''}
            onChange={handleInputChange}
            required
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label={t('expenseCategories.description') || 'Description'}
            name="description"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={currentCategory.description || ''}
            onChange={handleInputChange}
            sx={{ mt: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={currentCategory.isActive ?? true}
                onChange={handleSwitchChange}
                color="primary"
              />
            }
            label={t('expenseCategories.active') || 'Active'}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button onClick={handleSave} variant="contained">
            {t('common.save') || 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExpenseCategories;

